import { useState, useEffect } from 'react';
import { X, Send, Mail, Star, Moon, Calendar, Sparkles, Eye, Wand2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getIslamicEvents, getUpcomingEvents, IslamicEvent } from '../utils/islamicEvents';

interface Subscriber {
  id: string;
  email: string;
  name: string;
  city: string;
  country: string;
  unsubscribe_token?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  category: 'eid' | 'ramadan' | 'general' | 'hajj' | 'special';
  icon: any;
  eventId?: string;
}

interface Props {
  selectedSubscribers: Subscriber[];
  onClose: () => void;
  onSent: () => void;
}

// Helper function to create Islamic email footer
const getIslamicFooter = () => {
  return `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📱 Download Our Prayer Times App
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🌟 Get accurate prayer times, Islamic events, and notifications
🔗 Visit: https://xuber.ca

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🤲 This is Sadaqa Jariyah | صدقة جارية 🤲

Please remember in your duas the creator of this app:
Samer Hasoun

يرجى تذكر صانع هذا التطبيق في دعائك:
سامر حسون

May Allah reward him and make this a continuous charity for him and his family.
اللهم اجعل هذا العمل في ميزان حسناته

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💼 PROUDLY SPONSORED BY 💼
برعاية كريمة من

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🏡 THE PROPERTY COUSINS 🏡

We Make Dreams Come True!
نحقق الأحلام!

Visit: thepropertycousins.net
🌐 Your trusted real estate partners

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This application is made possible by the generous support of
The Property Cousins

هذا التطبيق برعاية The Property Cousins

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🕌 Prayer Times Team | فريق أوقات الصلاة 🕌
Serving the Muslim community with love
نخدم المجتمع الإسلامي بحب

JazakAllahu Khairan | جزاكم الله خيراً`;
};

export function EmailTemplates({ selectedSubscribers, onClose, onSent }: Props) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [sending, setSending] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState<IslamicEvent[]>([]);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showPreview, setShowPreview] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<IslamicEvent | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      const events = await getUpcomingEvents(60);
      setUpcomingEvents(events);
    };
    fetchEvents();
  }, []);

  const getTemplateForEvent = (event: IslamicEvent): EmailTemplate => {
    const iconMap: Record<string, any> = {
      'eid': Star,
      'ramadan': Moon,
      'hajj': Calendar,
      'special': Sparkles
    };

    const templateMap: Record<string, { subject: string; body: string }> = {
      'ramadan-start': {
        subject: '🌙 Ramadan Kareem! - رمضان كريم 🌙',
        body: `🌙✨ Ramadan Kareem! ✨🌙
رمضان كريم

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Assalamu Alaikum {{name}}, 💚

The blessed month of Ramadan has arrived! 🎉
May Allah accept your fasts, prayers, and all good deeds.

لقد حل علينا شهر رمضان المبارك! 🌙
بارك الله فيك وتقبل الله صيامك وقيامك

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🕌 PRAYER TIMES FOR {{location}}:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🌅 Fajr & Suhoor: Check your app for exact timing
🌆 Maghrib & Iftar: Get notified before sunset
🌙 Isha & Tarawih: Stay connected with the community

All timings available in your personalized prayer times app.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📿 RAMADAN REMINDERS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

✅ Keep your notifications enabled for timely reminders
📖 Recite Quran daily - aim to complete it this month
🤲 Make sincere duas during iftar time
💰 Give charity generously
👨‍👩‍👧‍👦 Share this app with family and friends

The Prophet (ﷺ) said: "When Ramadan comes, the gates of Paradise are opened, the gates of Hell are closed, and the devils are chained."

May Allah grant us the strength to make the most of this blessed month and accept our worship. 🤲

Ramadan Mubarak! 🌙✨
رمضان مبارك وكل عام وأنتم بخير${getIslamicFooter()}`
      },
      'ramadan-jummah': {
        subject: '🕌 First Jumu\'ah of Ramadan - أول جمعة في رمضان',
        body: `First Jumu'ah of Ramadan 🕌

Dear {{name}},

May this blessed Jumu'ah bring you closer to Allah!

أول جمعة في رمضان المبارك

Jumu'ah Prayer Time for {{location}}:
Check your prayer times app for exact timing.

Remember to make abundant dua today!

Ramadan Mubarak!
Prayer Times Team`
      },
      'ramadan-last10': {
        subject: '✨ Last 10 Nights of Ramadan Begin - العشر الأواخر',
        body: `Last 10 Nights of Ramadan Begin ✨

Dear {{name}},

The most blessed nights of Ramadan have arrived!

بداية العشر الأواخر من رمضان

Seek Laylat al-Qadr in these nights!

Prayer Times for {{location}}:
- Isha & Tarawih
- Tahajjud (Last third of night)
- Fajr
All available in your app.

Recommended acts:
- Recite Quran abundantly
- Make sincere dua
- Pray Tahajjud
- Give charity
- Seek forgiveness

May Allah accept your worship!
Prayer Times Team`
      },
      'laylat-qadr': {
        subject: '✨🌙 Laylat al-Qadr - ليلة القدر 🌙✨',
        body: `✨🌙 LAYLAT AL-QADR 🌙✨
The Night of Power
ليلة القدر

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Assalamu Alaikum {{name}}, 💚

Tonight is one of the MOST BLESSED nights of the entire year! 🎉
This is Laylat al-Qadr - The Night of Decree! 🌟

ليلة القدر خير من ألف شهر
"The Night of Decree is better than a thousand months" (Quran 97:3)

This one night is worth more than 83 YEARS of worship! 🤲

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🕌 PRAYER TIMES FOR {{location}}:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🌙 Isha: Check your app for exact timing
⭐ Tahajjud: Last third of the night (best time for dua!)
🌅 Fajr: Check your app for exact timing

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📿 HIGHLY RECOMMENDED ACTS TONIGHT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📖 Recite Quran abundantly
🤲 Make abundant dua for yourself, family, and all Muslims
🌟 Repeat this beautiful dua:

"Allahumma innaka 'afuwwun tuhibbul 'afwa fa'fu 'anni"
اللهم إنك عفو تحب العفو فاعف عني

O Allah, You are Most Forgiving, and You love forgiveness; so forgive me.

🙏 Pray Tahajjud (night prayer)
💰 Give charity - even a small amount
😢 Seek sincere forgiveness for all your sins
📱 Share this reminder with loved ones

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

The Prophet (ﷺ) said: "Whoever establishes prayers on Laylat al-Qadr out of sincere faith and hoping to attain Allah's rewards, all his past sins will be forgiven."

May Allah accept your worship, answer your duas, and grant you His infinite mercy. This is your chance to change your life! 🌙✨

اللهم تقبل منا وارحمنا${getIslamicFooter()}`
      },
      'eid-fitr': {
        subject: '🌙🎉 Eid Mubarak! - عيد مبارك 🎉🌙',
        body: `🌙🎉 EID AL-FITR MUBARAK! 🎉🌙
عيد الفطر المبارك

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Assalamu Alaikum {{name}}, 💚

On behalf of the entire Muslim community, we wish you and your family a BLESSED EID AL-FITR! 🎊

May Allah accept your fasts, prayers, night prayers, charity, and all good deeds during the blessed month of Ramadan. 🤲

عيد مبارك وكل عام وأنتم بخير
تقبل الله منا ومنكم صالح الأعمال

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🕌 EID PRAYER TIMES FOR {{location}}:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🌅 Check your prayer times app for exact Eid Salah timing
📍 Find your nearest mosque location
👨‍👩‍👧‍👦 Bring your family for this blessed gathering!

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🎁 EID REMINDERS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💰 Pay your Zakat al-Fitr before Eid prayer
🧼 Perform Ghusl (ritual bath)
👔 Wear your best clothes
😊 Greet everyone with smiles and duas
🍰 Enjoy halal treats and celebration
🤝 Visit family and strengthen bonds
💚 Forgive others and seek forgiveness

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

The Prophet (ﷺ) said: "On the day of Eid, Allah boasts about the worshippers to the angels."

May this Eid bring joy, happiness, peace, and countless blessings to your life. May all your prayers be answered! 🌟

Taqabbal Allahu minna wa minkum! 🤲
تقبل الله منا ومنكم

Eid Mubarak to you and your loved ones! 🎉✨
عيد سعيد${getIslamicFooter()}`
      },
      'dhul-hijjah-start': {
        subject: '⛰️ The Sacred Month of Dhul Hijjah - ذو الحجة',
        body: `The Sacred Month of Dhul Hijjah ⛰️

Dear {{name}},

The blessed month of Dhul Hijjah has begun!

بداية ذو الحجة المبارك

The Prophet (ﷺ) said: "There are no days in which righteous deeds are more beloved to Allah than these ten days."

Recommended Acts:
- Fast the first 9 days (especially Day of Arafah)
- Increase in dhikr and takbeer
- Give charity
- Seek forgiveness
- Make sincere dua

Prayer Times for {{location}}:
Available in your app.

May Allah accept your good deeds!
Prayer Times Team`
      },
      'arafah': {
        subject: '⛰️ Day of Arafah - يوم عرفة',
        body: `Day of Arafah ⛰️

Dear {{name}},

Today is the blessed Day of Arafah!

يوم عرفة - أفضل أيام السنة

The Prophet (ﷺ) said: "There is no day on which Allah frees more people from the Fire than the Day of Arafah."

Fasting Recommended:
For those not performing Hajj, fasting on the Day of Arafah expiates the sins of two years.

Prayer Times for {{location}}:
Check your app for exact timings.

Make abundant dua today - it is the best day for dua!

May Allah accept the Hajj of those at Arafah and the worship of those fasting.

Prayer Times Team`
      },
      'eid-adha': {
        subject: '🕌🐑 Eid al-Adha Mubarak! - عيد الأضحى مبارك 🐑🕌',
        body: `🕌🐑 EID AL-ADHA MUBARAK! 🐑🕌
عيد الأضحى المبارك

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Assalamu Alaikum {{name}}, 💚

Wishing you and your loved ones a BLESSED EID AL-ADHA! 🎊

May Allah accept your sacrifices, Hajj (if performed), prayers, and all acts of worship. 🤲

عيد أضحى مبارك وكل عام وأنتم بخير
تقبل الله منا ومنكم

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🕌 EID PRAYER TIMES FOR {{location}}:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🌅 Check your prayer times app for exact Eid Salah timing
📍 Gather with your community at the mosque
👨‍👩‍👧‍👦 Bring your whole family!

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🐑 THE SPIRIT OF SACRIFICE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This Eid commemorates Prophet Ibrahim's willingness to sacrifice his beloved son for Allah. It reminds us to:

💚 Submit completely to Allah's will
🤲 Make sacrifices for our faith
👨‍👩‍👧‍👦 Value family bonds
💰 Share blessings with the needy
🙏 Be grateful for Allah's infinite mercy

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🎁 EID REMINDERS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🧼 Perform Ghusl before Eid prayer
👔 Wear your finest clothes
🐑 Distribute Qurbani meat: 1/3 family, 1/3 friends, 1/3 poor
🕌 Pray Eid Salah with the community
😊 Spread joy and make others happy
🤝 Visit relatives and strengthen bonds
💚 Say Takbeer: Allahu Akbar!

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

The Prophet (ﷺ) said: "There is no deed more precious to Allah during these days than sacrifice."

May your faith and devotion be rewarded abundantly. May Allah accept your Qurbani and grant you His endless blessings! 🌟

Taqabbal Allahu minna wa minkum! 🤲
تقبل الله منا ومنكم

Eid Mubarak! 🎉✨
عيد مبارك${getIslamicFooter()}`
      },
      'islamic-new-year': {
        subject: '🌟 Islamic New Year 1448 - رأس السنة الهجرية',
        body: `Islamic New Year 1448 🌟

Dear {{name}},

Wishing you a blessed Islamic New Year!

كل عام وأنتم بخير - سنة هجرية مباركة ١٤٤٨

As we enter a new Hijri year, let us reflect on our faith and renew our commitment to Allah.

Prayer Times for {{location}}:
Continue to receive timely notifications for all your daily prayers.

May this year bring peace, prosperity, and spiritual growth.

Sana Hijriya Mubarak!

Prayer Times Team`
      },
      'ashura': {
        subject: '🕌 Day of Ashura - يوم عاشوراء',
        body: `Day of Ashura 🕌

Dear {{name}},

Today is the Day of Ashura (10th of Muharram).

يوم عاشوراء

The Prophet (ﷺ) said: "Fasting on the Day of Ashura, I hope, will expiate for the sins of the previous year."

Recommended:
- Fast on the 9th and 10th, or 10th and 11th of Muharram
- Make sincere dua
- Give charity

Prayer Times for {{location}}:
Check your app for exact timings.

May Allah accept your fasts and good deeds.

Prayer Times Team`
      },
      'mawlid': {
        subject: '🌟 Mawlid al-Nabi - المولد النبوي الشريف',
        body: `Mawlid al-Nabi 🌟

Dear {{name}},

Today we celebrate the birth of the Prophet Muhammad (ﷺ).

المولد النبوي الشريف

May we follow his example and embody his beautiful character.

صلى الله عليه وسلم

Prayer Times for {{location}}:
Continue to receive timely notifications for all your daily prayers.

May Allah's peace and blessings be upon the Prophet and his family.

Prayer Times Team`
      },
      'isra-miraj': {
        subject: '✨ Isra and Mi\'raj - الإسراء والمعراج',
        body: `Isra and Mi'raj ✨

Dear {{name}},

Tonight we commemorate the miraculous Night Journey and Ascension of the Prophet Muhammad (ﷺ).

الإسراء والمعراج

On this blessed night, the five daily prayers were prescribed.

Prayer Times for {{location}}:
Check your app for exact timings.

May we always fulfill our prayers on time.

Prayer Times Team`
      },
      'shaban': {
        subject: '🌙 Laylat al-Bara\'ah - ليلة البراءة',
        body: `Laylat al-Bara'ah 🌙

Dear {{name}},

Tonight is the Night of Forgiveness (15th of Sha'ban).

ليلة البراءة - ليلة النصف من شعبان

This blessed night is a time for:
- Seeking forgiveness
- Making sincere dua
- Praying nafl prayers
- Reciting Quran

Prayer Times for {{location}}:
Check your app for exact timings.

May Allah forgive our sins and accept our worship.

Prayer Times Team`
      }
    };

    const template = templateMap[event.id] || {
      subject: `✨ ${event.name} - ${event.nameArabic} ✨`,
      body: `✨ ${event.name.toUpperCase()} ✨
${event.nameArabic}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Assalamu Alaikum {{name}}, 💚

${event.description}

${event.nameArabic}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🕌 PRAYER TIMES FOR {{location}}:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Check your prayer times app for exact timings and notifications.
Stay connected with your faith throughout the day! 🌙

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

May Allah bless you and your family with His infinite mercy and barakah. 🤲

بارك الله فيك${getIslamicFooter()}`
    };

    return {
      id: event.id,
      name: event.name,
      subject: template.subject,
      body: template.body,
      category: event.type,
      icon: iconMap[event.type] || Calendar,
      eventId: event.id
    };
  };

  const eventTemplates = upcomingEvents.map(getTemplateForEvent);

  const staticTemplates: EmailTemplate[] = [
    {
      id: 'general-reminder',
      name: 'General Prayer Reminder',
      subject: '🕌 Prayer Time Reminder - تذكير بأوقات الصلاة 🕌',
      body: `🕌 PRAYER TIME REMINDER 🕌
تذكير بأوقات الصلاة

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Assalamu Alaikum {{name}}, 💚

This is a friendly reminder to keep your prayers on time! 🕒

The Prophet (ﷺ) said: "The first matter that the slave will be brought to account for on the Day of Judgment is the prayer. If it is sound, then the rest of his deeds will be sound."

حَافِظُوا عَلَى الصَّلَوَاتِ وَالصَّلَاةِ الْوُسْطَىٰ
"Maintain with care the [obligatory] prayers" (Quran 2:238)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🕌 PRAYER TIMES FOR {{location}}:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🌅 Fajr - Before sunrise
☀️ Dhuhr - Midday prayer
🌤️ Asr - Afternoon prayer
🌇 Maghrib - Sunset prayer
🌙 Isha - Night prayer

Check your prayer times app for exact timings and get notified before each prayer!

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📱 REMINDER:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

✅ Keep your notifications enabled for timely reminders
🕌 Pray in the mosque whenever possible (27x more reward!)
🤲 Make dua after each prayer
📖 Recite dhikr after salah

May Allah accept your prayers and make them a source of peace and guidance. 🤲

اللهم تقبل صلاتنا${getIslamicFooter()}`,
      category: 'general',
      icon: Mail
    },
    {
      id: 'custom',
      name: 'Custom Message',
      subject: '',
      body: getIslamicFooter(),
      category: 'general',
      icon: Mail
    }
  ];

  const allTemplates = [...eventTemplates, ...staticTemplates];

  const filteredTemplates = filterCategory === 'all'
    ? allTemplates
    : allTemplates.filter(t => t.category === filterCategory);

  const handleTemplateSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template.id);
    setCustomSubject(template.subject);
    setCustomBody(template.body);

    const event = upcomingEvents.find(e => e.id === template.eventId);
    setSelectedEvent(event || null);
    setShowPreview(false);
  };

  const getEventCountdown = (event: IslamicEvent) => {
    const now = new Date();
    const diff = event.date.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      return 'Today!';
    }
  };

  const generatePreviewContent = () => {
    const sampleSubscriber = selectedSubscribers[0] || {
      name: 'Sample User',
      city: 'Windsor',
      country: 'Canada'
    };

    let previewSubject = customSubject
      .replace(/\{\{name\}\}/g, sampleSubscriber.name)
      .replace(/\{\{location\}\}/g, `${sampleSubscriber.city}, ${sampleSubscriber.country}`);

    let previewBody = customBody
      .replace(/\{\{name\}\}/g, sampleSubscriber.name)
      .replace(/\{\{location\}\}/g, `${sampleSubscriber.city}, ${sampleSubscriber.country}`);

    return { previewSubject, previewBody };
  };

  const handleSend = async () => {
    if (!customSubject || !customBody) {
      alert('Please fill in subject and body');
      return;
    }

    setSending(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-batch-notifications', {
        body: {
          subscribers: selectedSubscribers,
          subject: customSubject,
          bodyTemplate: customBody
        }
      });

      if (error) throw error;

      alert(`Email sent successfully to ${selectedSubscribers.length} subscribers!`);
      onSent();
    } catch (error) {
      console.error('Error sending emails:', error);
      alert('Failed to send emails. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const { previewSubject, previewBody } = generatePreviewContent();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-teal-800 to-teal-900 rounded-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-white/10 backdrop-blur-md p-6 flex items-center justify-between border-b border-white/20 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-white">Send Email Notification</h2>
            <p className="text-white/70 text-sm">{selectedSubscribers.length} recipients selected</p>
          </div>
          <div className="flex items-center gap-3">
            {selectedTemplate && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-semibold"
              >
                <Eye className="w-4 h-4" />
                {showPreview ? 'Edit' : 'Preview'}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {selectedEvent && (
            <div className="mb-6 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-500/50 rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-xl mb-1">{selectedEvent.name}</h3>
                  <p className="text-white/80 text-lg">{selectedEvent.nameArabic}</p>
                  <p className="text-white/70 text-sm mt-2">{selectedEvent.description}</p>
                </div>
                <div className="text-right">
                  <div className="bg-white/20 backdrop-blur-md rounded-lg px-4 py-3">
                    <div className="text-white/70 text-xs mb-1">Countdown</div>
                    <div className="text-white font-bold text-xl">{getEventCountdown(selectedEvent)}</div>
                    <div className="text-white/80 text-sm mt-1">{selectedEvent.date.toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!selectedEvent && upcomingEvents.length > 0 && (
            <div className="mb-6 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Upcoming Islamic Events ({upcomingEvents.length})
              </h4>
              <div className="text-white/80 text-sm">
                Templates available for events in the next 60 days
              </div>
            </div>
          )}

          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold text-sm">Filter:</span>
            {['all', 'ramadan', 'eid', 'hajj', 'special', 'general'].map((cat) => (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                  filterCategory === cat
                    ? 'bg-amber-500 text-white'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Choose a Template</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-2">
              {filteredTemplates.map((template) => {
                const Icon = template.icon;
                const isEventTemplate = template.eventId !== undefined;
                return (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`p-4 rounded-xl text-left transition-all ${
                      selectedTemplate === template.id
                        ? 'bg-amber-500 text-white'
                        : isEventTemplate
                        ? 'bg-gradient-to-r from-green-500/20 to-teal-500/20 border border-green-500/30 text-white hover:border-green-400'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="w-6 h-6 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{template.name}</div>
                        {template.subject && (
                          <div className="text-sm opacity-80 truncate mt-1">{template.subject}</div>
                        )}
                        {isEventTemplate && (
                          <div className="text-xs opacity-70 mt-1">Upcoming Event</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedTemplate && (
            <div className="space-y-4">
              {!showPreview ? (
                <>
                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-4">
                    <label className="block text-white font-semibold mb-2 flex items-center gap-2">
                      <Wand2 className="w-5 h-5" />
                      AI Customization Prompt
                    </label>
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      rows={2}
                      className="w-full bg-white/10 text-white border border-white/30 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-white/40"
                      placeholder="e.g., 'Make it more personal and warm', 'Add a quote about charity', 'Focus on family unity'"
                    />
                    <p className="text-white/60 text-xs mt-2">
                      Note: This is a placeholder for AI integration. Manually edit the template below.
                    </p>
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Subject</label>
                    <input
                      type="text"
                      value={customSubject}
                      onChange={(e) => setCustomSubject(e.target.value)}
                      className="w-full bg-white/10 text-white border border-white/30 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-white/40"
                      placeholder="Email subject..."
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">
                      Message Body
                      <span className="text-sm font-normal opacity-70 ml-2">
                        (Use {`{{name}}`} and {`{{location}}`} for personalization)
                      </span>
                    </label>
                    <textarea
                      value={customBody}
                      onChange={(e) => setCustomBody(e.target.value)}
                      rows={14}
                      className="w-full bg-white/10 text-white border border-white/30 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono text-sm placeholder-white/40"
                      placeholder="Email body..."
                    />
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-xl p-8 shadow-2xl">
                  <div className="border-b border-gray-200 pb-4 mb-6">
                    <div className="text-xs text-gray-500 mb-2">From: Prayer Times Team</div>
                    <div className="text-xs text-gray-500 mb-2">
                      To: {selectedSubscribers[0]?.name} ({selectedSubscribers[0]?.email})
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mt-4">{previewSubject}</h3>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    {previewBody.split('\n').map((line, i) => (
                      <p key={i} className="text-gray-700 leading-relaxed">
                        {line || <br />}
                      </p>
                    ))}
                  </div>
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      This email will be sent to {selectedSubscribers.length} recipient{selectedSubscribers.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2">Recipients ({selectedSubscribers.length})</h4>
                <div className="text-white/70 text-sm space-y-1 max-h-32 overflow-y-auto">
                  {selectedSubscribers.slice(0, 10).map(sub => (
                    <div key={sub.id}>
                      {sub.name} ({sub.email}) - {sub.city}, {sub.country}
                    </div>
                  ))}
                  {selectedSubscribers.length > 10 && (
                    <div className="text-amber-400">+ {selectedSubscribers.length - 10} more...</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white/10 backdrop-blur-md p-6 border-t border-white/20 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!selectedTemplate || sending}
            className="px-6 py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white rounded-lg transition-all flex items-center gap-2 font-semibold"
          >
            {sending ? (
              <>Sending...</>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
